<template>
  <div>
    <form @submit.prevent="handleSubmit" v-shortkey="saveShortkey" @shortkey="handleSubmit">
      <h2>{{ formTitle }}</h2>
      <SelectInput
        :validator="$v.type"
        :items="types"
        v-model="type"
        label="Edit Type"
        :disabled="types.length === 1"
      />
      <div v-if="isSecondaryMacro">
        <div
          v-for="specimen of specimenSecondaryResultsMacros"
          v-bind:key="specimen.id"
          class="row py-2"
        >
          <b class="col-1 d-flex align-items-center text-end"> {{ specimen.specimenOrder }}: </b>
          <TagInput
            class="col-11"
            :noLabel="true"
            :dataSource="resultsMacrosDataSource"
            valueExpr="macroId"
            displayExpr="macroName"
            v-model="specimen.secondaryResultsMacros"
          />
        </div>
      </div>
      <text-area-input
        v-else
        :validator="$v.reasonForChange"
        :resize="false"
        ref="reasonForChange"
        rows="5"
        label="Reason"
        v-model="reasonForChange"
        :generalMacrosEnabled="true"
        :startHeight="propReason ? '25vw' : 'auto'"
        :width="propReason ? '80vw' : '100%'"
      />
      <div class="d-flex justify-content-end align-items-end">
        <button type="button" @click="cancel" class="btn btn-danger">Cancel</button>
        <button type="submit" class="btn mx-1 btn-primary">Submit</button>
      </div>
    </form>
  </div>
</template>

<script>
import { required } from "vuelidate/lib/validators";
import TextAreaInput from "./TextAreaInput.vue";
import { mapGetters, mapState } from "vuex";
import SelectInput from "@/components/common/SelectInput.vue";
import { altKey, dateRangeFilter, createLogItem, findEnumValue } from "@/modules/helpers";
import { CasesApi, MacrosApi, SpecimensApi, AuditLogApi } from "../services";
import { MacroTypeEnum, CaseEditTypeEnum, AuditLogItems } from "@/modules/enums";
import { handleErrors } from "@/modules/handleErrors";
import TagInput from "@/components/common/TagInput.vue";
import DataSource from "devextreme/data/data_source";

export default {
  components: { TextAreaInput, SelectInput, TagInput },
  props: ["propReason"],
  data() {
    return {
      type: "",
      reasonForChange: "",
      saveShortkey: altKey("s"),
      isMacroOpen: false,
      positionToPlaceCursor: 0,
      generalMacros: [],
      specimenSecondaryResultsMacros: []
    };
  },
  mounted() {
    this.type = this.$store.state.accessionStore.editType;
    if (this.propReason) {
      this.reasonForChange = this.propReason.reasonForChange;
      this.type = this.propReason.editType;
    } else {
      this.reasonForChange = this.$store.state.accessionStore.reasonForChange;
      if (
        !this.permissions.CaseEditAfterReportedDiagnostic &&
        !this.labSettings.AllowCaseEditTypeBilling
      ) {
        this.type = 4;
      }
    }
    MacrosApi.getMacrosByUserAndType({
      userId: this.currentUser.id,
      macroTypeId: MacroTypeEnum.General,
      loadOptions: {}
    }).then(res => {
      this.generalMacros = res.data || [];
    });
  },
  computed: {
    ...mapState({
      reReportPopup: state => state.accessionStore.reReportPopup,
      labSettings: state => state.labSettings,
      currentUser: state => state.currentUser,
      specimens: state => state.accessionStore.specimens,
      caseDetails: state => state.accessionStore.caseDetails
    }),
    ...mapGetters(["permissions"]),
    ...mapGetters("accessionStore", ["isCytology"]),
    types() {
      let typeArray = [];
      if (this.permissions.CaseEditAfterReportedDiagnostic) {
        typeArray = [
          {
            id: CaseEditTypeEnum.Amended,
            displayName: "Amended"
          },
          {
            id: CaseEditTypeEnum.Addendum,
            displayName: "Addendum"
          },
          {
            id: CaseEditTypeEnum.Corrected,
            displayName: "Corrected"
          },
          { id: CaseEditTypeEnum.NonDiagnostic, displayName: "NonDiagnostic" }
        ];
      } else if (this.permissions.CaseEditAfterReportedNondiagnostic) {
        typeArray = [{ id: CaseEditTypeEnum.NonDiagnostic, displayName: "NonDiagnostic" }];
      }
      if (this.labSettings.AllowCaseEditTypeBilling) {
        typeArray.push({ id: CaseEditTypeEnum.Billing, displayName: "Billing" });
      }
      if (this.labSettings.AllowCaseEditReasonOther) {
        typeArray.push({ id: CaseEditTypeEnum.Other, displayName: "Other" });
      }
      if (this.labSettings.UseSecondaryDiagnosisMacros) {
        typeArray.push({ id: CaseEditTypeEnum.SecondaryMacro, displayName: "Secondary Macro" });
      }
      return typeArray;
    },
    isSecondaryMacro() {
      return this.type === CaseEditTypeEnum.SecondaryMacro;
    },
    resultsMacrosDataSource() {
      return new DataSource({
        store: MacrosApi.searchStore,
        filter: [
          dateRangeFilter("effectiveOn", "expiresOn"),
          "and",
          ["macroType", MacroTypeEnum.Results]
        ],
        sort: "macroName"
      });
    },
    formTitle() {
      if (this.propReason) {
        return `${this.propReason?.id ? "Edit" : "Add"} Reason for Change`;
      }
      return "Re-Report";
    }
  },
  validations() {
    return {
      type: {
        required
      },
      reasonForChange: {
        required
      }
    };
  },
  watch: {
    specimens: {
      immediate: true,
      handler(nv, ov) {
        if (nv?.length && !ov?.length) {
          this.loadSpecimens();
        }
      }
    }
  },
  methods: {
    handleSubmit() {
      if (this.isSecondaryMacro) {
        this.handleAddSecondaryMacros();
      } else {
        this.handleReReport();
      }
    },
    async handleReReport() {
      this.$v.$touch();
      if (this.$v.$invalid) {
        window.notify("Missing required fields, please try again.", "warning");
        return;
      }
      if (this.propReason) {
        if (this.propReason?.id) {
          // Edit Reason for Change
          try {
            await CasesApi.editEditReason({
              caseId: this.propReason.caseId,
              id: this.propReason.id,
              editTypeId: this.type,
              reasonForChange: this.reasonForChange
            });
            window.notify("Reason for Change updated.");
            this.$emit("edit");
          } catch (error) {
            handleErrors(error);
          }
        } else {
          // Add new Reason for Change
          try {
            const payload = {
              caseId: this.propReason.caseId,
              editType: this.type,
              reasonForChange: this.reasonForChange
            };
            await CasesApi.addEditReason(payload);
            window.notify("Added Reason for Change.");
            const logItem = createLogItem(this.caseDetails, AuditLogItems.ChangeAccession);
            logItem.comments = `Manually added edit reason to case:\n\n${JSON.stringify(
              { ...payload, editType: findEnumValue(payload.editType, CaseEditTypeEnum) },
              null,
              2
            )}`;
            AuditLogApi.insertLogMessage(logItem);
            this.$emit("edit");
          } catch (error) {
            handleErrors(error);
          }
        }
      } else {
        // Set edit reason for case
        if ([1, 2, 3].includes(this.type)) {
          if (this.isCytology && this.$route.name !== "CytologyResults") {
            this.$router.push({ ...this.$route, name: "CytologyResults" });
          } else if (this.$route.name !== "SpecimenResults") {
            this.$router.push({ ...this.$route, name: "SpecimenResults" });
          }
        } else if (this.$route.name !== "CaseView") {
          this.$router.push({ ...this.$route, name: "CaseView" });
        }
        this.$store.dispatch("accessionStore/ammendCase", {
          editType: this.type,
          reasonForChange: this.reasonForChange
        });
      }
    },
    cancel() {
      if (this.propReason?.id) {
        this.$emit("cancel");
      } else this.$store.commit("accessionStore/setReReportPopup", false);
    },
    loadSpecimens() {
      this.specimenSecondaryResultsMacros = this.specimens.map(e => {
        return { id: e.id, specimenOrder: e.specimenOrder, secondaryResultsMacros: [] };
      });
    },
    async handleAddSecondaryMacros() {
      let payload = [];
      for (const specimen of this.specimenSecondaryResultsMacros) {
        for (const macroId of specimen.secondaryResultsMacros) {
          payload.push({ specimenId: specimen.id, macroId });
        }
      }
      if (!payload.length) {
        window.alert("Please select macros for at least one specimen.");
        return;
      }
      try {
        await SpecimensApi.addSecondaryResultsMacros(payload);
        window.notify("Added secondary results macros to specimens.");
        this.$store.dispatch("accessionStore/getCaseHeader", this.caseDetails.caseId);
        this.$store.dispatch("accessionStore/getCaseSpecimens", this.caseDetails.caseId);
        this.cancel();
      } catch (error) {
        handleErrors(error);
      }
    }
  }
};
</script>

<style lang="scss" scoped></style>
